/* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-call, typescript/no-unsafe-return -- react-keyed-flatten-children lacks proper types for component.type */
import { isValidElement, cloneElement, Children } from 'react'
import type * as React from 'react'

export function isIcon(child: React.ReactNode): boolean {
  return (
    isValidElement(child) &&
    typeof (child.type as any) === 'function' &&
    typeof (child.type as any).displayName === 'string' &&
    (child.type as any).displayName.endsWith('Icon')
  )
}

export function isLabel(child: React.ReactNode): boolean {
  return typeof child === 'string' || typeof child === 'number'
}

export function processIcon(
  icon: React.ReactNode,
  size: number,
): React.ReactNode {
  if (!(isValidElement(icon) && isIcon(icon))) return icon
  const iconProps: any = { ...(icon.props as any), size }
  return cloneElement(icon as any, iconProps)
}

export function getButtonIconSize({
  size,
  variant,
  defaultContainedSize,
}: {
  size?: 'sm' | 'default' | 'lg'
  variant?: 'text' | 'filled' | 'outlined'
  defaultContainedSize?: number
}): number | undefined {
  if (size === 'sm') return variant === 'filled' ? 14 : 18
  if (size === 'lg') return 28
  if (variant === 'filled' || variant === 'outlined')
    return defaultContainedSize
  return undefined
}

/** Recursively finds an icon element (displayName ending in "Icon") in the tree */
export function findIcon(node: React.ReactNode): React.ReactElement | null {
  if (!node) return null

  if (isIcon(node)) {
    return node as React.ReactElement
  }

  if (isValidElement(node) && (node.props as any).children) {
    const children = (node.props as any).children
    const childArray = Children.toArray(children)
    for (const child of childArray) {
      const found = findIcon(child)
      if (found) return found
    }
  }

  if (Array.isArray(node)) {
    for (const child of node) {
      const found = findIcon(child)
      if (found) return found
    }
  }

  return null
}

/** Recursively extracts all text content from the tree */
export function extractTextContent(node: React.ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)

  if (isValidElement(node) && (node.props as any).children) {
    const children = (node.props as any).children
    const childArray = Children.toArray(children)
    // oxlint-disable-next-line unicorn/no-array-callback-reference -- passing named fn as callback is the intended shape
    return childArray.map(extractTextContent).filter(Boolean).join('')
  }

  if (Array.isArray(node)) {
    // oxlint-disable-next-line unicorn/no-array-callback-reference -- passing named fn as callback is the intended shape
    return node.map(extractTextContent).filter(Boolean).join('')
  }

  return ''
}

/** Checks if the tree contains any text content */
export function hasTextContent(node: React.ReactNode): boolean {
  return extractTextContent(node).length > 0
}

interface ProcessChildrenOptions {
  iconSize?: number
  iconFill?: string
}

/** Recursively processes children, transforming icons with size/fill */
export function processChildrenTree(
  node: React.ReactNode,
  options: ProcessChildrenOptions,
): React.ReactNode {
  if (!node) return node

  // Process icon if found
  if (isIcon(node) && isValidElement(node)) {
    const iconProps: any = { ...(node.props as any) }
    if (options.iconSize !== undefined) iconProps.size = options.iconSize
    if (options.iconFill !== undefined) iconProps.fill = options.iconFill
    return cloneElement(node as any, iconProps)
  }

  // Recurse into element children
  if (isValidElement(node) && (node.props as any).children) {
    const originalChildren = (node.props as any).children
    // Use Children.toArray to preserve keys, then process
    const processedChildren = Children.toArray(originalChildren).map((child) =>
      processChildrenTree(child, options),
    )
    // Pass processed children as single array to avoid key issues
    return cloneElement(node as any, { children: processedChildren })
  }

  // Handle arrays - use Children.toArray to normalize and preserve keys
  if (Array.isArray(node)) {
    return Children.toArray(node).map((child) =>
      processChildrenTree(child, options),
    )
  }

  return node
}
